import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useInvalidatingMutation, useToastMutation } from '../utils/mutation';
import { onAngularEvent } from '../../services/angular-bridge';
import { notifyInfo } from '../../services/notify';
import type { Collection as DomainCollection, CreateCollectionBody } from '../../types/collection';

export interface Collection {
  id: string;
  projectId: string;
  name: string;
  status: string;
}

export function useCollections(projectId: string) {
  return useQuery<Collection[]>({
    queryKey: ['collections', projectId],
    queryFn: () => api.get<Collection[]>(`/project/${projectId}/collection`),
    enabled: !!projectId,
  });
}

export function useCreateCollection(projectId: string) {
  return useInvalidatingMutation<DomainCollection, CreateCollectionBody>(
    ['project', projectId],
    (body) => api.post<DomainCollection>(`/project/${projectId}/collection`, body)
  );
}

// Collections render off the project query (['project', projectId]), so resume/rename
// invalidate that key. Errors ride the global handler; only resume has a success toast.
export function useResumeCollection(projectId: string) {
  return useToastMutation(
    ['project', projectId],
    (collectionId: string) => api.post(`/project/${projectId}/collection/${collectionId}/process/resume`),
    {
      success: () => ({ title: 'Resuming collection processing', description: '', timeout: 5000 }),
    }
  );
}

export function useRenameCollection(projectId: string) {
  return useInvalidatingMutation(
    ['project', projectId],
    ({ collection, name }: { collection: DomainCollection; name: string }) =>
      api.put(`/project/${projectId}/collection/${collection.id}`, { ...collection, name })
  );
}

// Toast + refresh when collection processing is paused for exceeding the trial data limit
// (FeedService.ProcessingPaused). Mount once on the Collections tab.
export function useCollectionEventToasts(projectId: string) {
  const queryClient = useQueryClient();
  useEffect(() => {
    return onAngularEvent('FeedService.ProcessingPaused', (message: { name: string }) => {
      notifyInfo({
        title: 'Collection Processing Paused',
        description: `Collection ${message.name} was paused because you've exceeded the allowed 5GB data limit on your trial account, please activate to resume processing.`,
        timeout: 5000,
      });
      // CollectionsTab renders project.collections, so the project query is the source of truth.
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['collections', projectId] });
    });
  }, [projectId, queryClient]);
}
